import { useState, MouseEvent } from "react";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { DialogContent } from "@remo-co/ui-core/src/components/DialogContent";
import { DialogTitle } from "@remo-co/ui-core/src/components/DialogTitle";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { Fade } from "@remo-co/ui-core/src/components/Fade";
import { SlideProps } from "@remo-co/ui-core/src/components/Slide";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { CopyEventUrl, VideoTutorials } from "modules/event/components";
import ShareEvent from "modules/eventLanding/ShareEvent";
import { IEvent } from "modules/event/types";
import { ChevronLeft } from "@remo-co/ui-core/src/icons/ChevronLeft";
import { Close } from "@remo-co/ui-core/src/icons/Close";
import { PlayCircle as PlayCircleIcon } from "@remo-co/ui-core/src/icons/PlayCircle";
import { map } from "lodash";
import VIDEO_TUTORIALS from "modules/tutorials/videos";
import { useI18n } from "i18n";
import { useHistory } from "react-router";
import { useStyles } from "./styles";

interface Props {
  event: IEvent;
  onClose: () => void;
}

const FadeTransition = (props: SlideProps) => <Fade {...props} timeout={300} />;

const CreateEventSuccess = ({ event, onClose }: Props) => {
  const styles = useStyles();
  const { t } = useI18n(["common", "event"]);
  const { concurrentEvents, privateEvent, customFloorPlan } = VIDEO_TUTORIALS;
  const createEventsTutorials = {
    [t(privateEvent.title)]: privateEvent.url,
    [t(concurrentEvents.title)]: concurrentEvents.url,
    [t(customFloorPlan.title)]: customFloorPlan.url,
  };
  const [selectedTutorial, setSelectedTutorial] = useState<string | null>(null);
  const { push } = useHistory();

  const openTutorials = (e: MouseEvent, title: string) => {
    e.stopPropagation();
    e.preventDefault();

    setSelectedTutorial(title);
  };

  const onTutorialClose = () => {
    setSelectedTutorial(null);
  };

  return (
    <Dialog
      TransitionComponent={FadeTransition}
      open
      onClose={onClose}
      className={styles.createEventSuccess}
      maxWidth="xs"
    >
      <DialogTitle className={styles.title}>
        <IconButton
          aria-label={t("modal.window.aria.label")}
          onClick={onClose}
          size="large"
        >
          <Close />
        </IconButton>
        {selectedTutorial && (
          <IconButton
            aria-label={t("modal.window.aria.label")}
            onClick={onTutorialClose}
            size="large"
          >
            <ChevronLeft />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <Box className={selectedTutorial ? styles.vt : ""}>
          {selectedTutorial ? (
            <VideoTutorials
              title={selectedTutorial}
              url={createEventsTutorials[selectedTutorial]}
            />
          ) : (
            <>
              <>
                <Typography variant="h2">
                  {t("event:congratulations")}
                </Typography>
                <Typography variant="h5">
                  {t("event:create.success.1")}
                </Typography>
                <Typography variant="h5">
                  {t("event:create.success.2")}
                </Typography>
              </>

              <Container>
                <Button
                  variant="primary"
                  color="blue"
                  size="md"
                  onClick={() => push(`/e/${event.code}`)}
                  data-testid="success-cta-link"
                  contentClassName={styles.enterEventButton}
                >
                  {t("event:create.enter.event")}
                </Button>
              </Container>

              <>
                <Typography variant="h5">
                  {t("event:create.success.copy.url")}
                </Typography>
                <CopyEventUrl
                  event={event}
                  copyLinkId="event-create_secondary-cta-copy-link"
                />{" "}
              </>

              {!event.isPrivate && (
                <ShareEvent eventData={event} showText={false} />
              )}
            </>
          )}
        </Box>
        <Box
          className={`${styles.ceTutorials} ${
            selectedTutorial ? styles.vt : ""
          }`}
        >
          <Typography className={styles.eventTutorialText} variant="h5">
            {t("event:video.tutorial")}
          </Typography>
          {map(createEventsTutorials, (url: string, title: string) => (
            <Button<"a">
              rel="noopener noreferrer"
              variant="text"
              color="dark"
              href={url}
              onClick={(e) => openTutorials(e, title)}
              target="_blank"
              startAdornment={
                <PlayCircleIcon className={styles.eventTutorialButtonIcon} />
              }
            >
              {title}
            </Button>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreateEventSuccess;
